import { motion } from "framer-motion";
import { 
  LayoutDashboard, 
  CreditCard, 
  BrainCircuit, 
  PieChart, 
  Settings,
  LogOut,
  BookOpen,
  Search,
  Command,
  CalendarDays,
  FlaskConical,
  Landmark,
  Bell,
  ShieldCheck,
  Wand2,
  Users,
  Inbox,
  Download
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { SettingsModal } from "./SettingsModal";
import { showSuccess, showError } from "@/utils/toast";
import { useUser, useClerk } from "@clerk/clerk-react";
import { backendFetch } from "@/lib/backend";

const navItems = [
  { icon: LayoutDashboard, label: "Overview", path: "/dashboard" },
  { icon: CreditCard, label: "Subscriptions", path: "/subscriptions" },
  { icon: Wand2, label: "Future Stack", path: "/future-stack" },
  { icon: CalendarDays, label: "Timeline", path: "/calendar" },
  { icon: BrainCircuit, label: "Intelligence", path: "/intelligence" },
  { icon: Inbox, label: "Decision Inbox", path: "/decision-inbox" },
  { icon: PieChart, label: "Analytics", path: "/analytics" },
  { icon: FlaskConical, label: "Scenarios", path: "/scenarios" },
  { icon: Landmark, label: "Accounts", path: "/accounts" },
  { icon: Users, label: "Teams", path: "/teams" },
  { icon: BookOpen, label: "Vision", path: "/vision" },
  { icon: ShieldCheck, label: "Readiness", path: "/admin/readiness" },
];

export const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const { user } = useUser();
  const { signOut } = useClerk();

  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      return;
    }

    let cancelled = false;

    const syncUnreadCount = async (forceRefresh = false) => {
      try {
        const data = await backendFetch('/notifications', {
          forceRefresh,
          staleTtlMs: 15_000,
        });
        if (!cancelled) {
          const unread = (data.notifications || []).filter((note: { read?: boolean }) => !note.read).length;
          setUnreadCount(unread);
        }
      } catch {
        if (!cancelled) {
          setUnreadCount(0);
        }
      }
    };

    syncUnreadCount();
    const interval = window.setInterval(() => syncUnreadCount(true), 20000);
    const handleNotificationsChanged = (event: Event) => {
      const unread = (event as CustomEvent<{ unreadCount?: number }>).detail?.unreadCount;
      if (typeof unread === "number") {
        setUnreadCount(unread);
        return;
      }
      syncUnreadCount(true);
    };
    document.addEventListener("notifications-changed", handleNotificationsChanged);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener("notifications-changed", handleNotificationsChanged);
    };
  }, [user]);

  const openCommandPalette = () => {
    document.dispatchEvent(new CustomEvent('open-command-palette'));
  };

  const openNotifications = () => {
    document.dispatchEvent(new CustomEvent('open-notifications'));
  };

  const handleLogout = async () => {
    try {
      await signOut(() => navigate("/"));
      showSuccess("Logged out securely");
    } catch {
      showError("Failed to log out. Please try again.");
    }
  };

  const displayName = user?.fullName || user?.primaryEmailAddress?.emailAddress?.split('@')[0] || 'User';
  const initials = displayName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const userEmail = user?.primaryEmailAddress?.emailAddress || '';

  return (
    <>
      <SettingsModal isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} />
      
      <div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-[#040705]/90 backdrop-blur-xl border-b border-white/[0.05] z-40 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <img src="/orizonte-logo.png" alt="Orizonte" className="w-8 h-8 object-contain rounded-full" />
          <span className="font-semibold text-lg tracking-tight text-white">Orizonte</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={openNotifications} className="p-2 text-orizonte-textMuted hover:text-white transition-colors relative">
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute top-1.5 right-2 min-w-4 h-4 px-1 rounded-full bg-orizonte-lime text-[10px] leading-4 text-black font-bold shadow-[0_0_8px_rgba(204,255,0,0.8)]">
                {unreadCount}
              </span>
            )}
          </button>
        </div>
      </div>
      
      <aside className="fixed left-0 top-0 z-50 hidden h-dvh max-h-dvh w-64 flex-col overflow-hidden border-r border-orizonte-border bg-orizonte-bg/50 backdrop-blur-xl md:flex">
        <div className="p-6 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <img src="/orizonte-logo.png" alt="Orizonte" className="w-8 h-8 object-contain rounded-full" />
            <span className="font-semibold text-lg tracking-tight text-white">Orizonte</span>
          </div>
        </div>

        {user && (
          <div className="px-4 pb-4 shrink-0">
            <div className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.05]">
              {user.imageUrl ? (
                <img
                  src={user.imageUrl}
                  alt={displayName}
                  className="w-8 h-8 rounded-full object-cover shrink-0"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orizonte-lime to-orizonte-emerald flex items-center justify-center text-xs font-bold text-black shrink-0">
                  {initials}
                </div>
              )}
              <div className="min-w-0">
                <p className="text-sm font-medium text-orizonte-text truncate">{displayName}</p>
                <p className="text-[10px] text-orizonte-textMuted truncate">{userEmail}</p>
              </div>
            </div>
          </div>
        )}

        <div className="px-4 pb-4 shrink-0">
          <button 
            onClick={openCommandPalette}
            className="w-full flex items-center justify-between px-3 py-2.5 bg-white/[0.02] hover:bg-white/[0.05] border border-white/[0.05] rounded-lg text-orizonte-textMuted hover:text-orizonte-text transition-colors group"
          >
            <div className="flex items-center gap-2 text-sm">
              <Search className="w-4 h-4" />
              <span>Search stack</span>
            </div>
            <div className="hidden lg:flex items-center gap-0.5 text-xs bg-white/5 px-1.5 py-0.5 rounded border border-white/10 opacity-70 group-hover:opacity-100 transition-opacity">
              <Command className="w-3 h-3" /> K
            </div>
          </button>
        </div>

        <nav className="min-h-0 flex-1 overscroll-contain px-4 py-2 space-y-1 overflow-y-auto [WebkitOverflowScrolling:touch]">
          {navItems.map((item, index) => {
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.label}
                to={item.path}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-300 group relative",
                  isActive 
                    ? "bg-white/[0.04] text-orizonte-lime" 
                    : "text-orizonte-textMuted hover:bg-white/[0.02] hover:text-orizonte-text"
                )}
              >
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.4 }}
                  className="flex items-center gap-3 w-full"
                >
                  <item.icon className={cn(
                    "w-5 h-5 transition-colors",
                    isActive ? "text-orizonte-lime" : "text-orizonte-textMuted group-hover:text-orizonte-text"
                  )} />
                  <span className="font-medium text-sm">{item.label}</span>
                </motion.div>
                {isActive && (
                  <motion.div 
                    layoutId="active-pill"
                    className="absolute left-0 w-1 h-8 bg-orizonte-lime rounded-r-full shadow-[0_0_10px_rgba(204,255,0,0.5)]" 
                  />
                )}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-orizonte-border/50 space-y-1 shrink-0">
          <button 
            onClick={openNotifications}
            className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-orizonte-textMuted hover:bg-white/[0.02] hover:text-orizonte-text transition-all duration-300"
          >
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5" />
              <span className="font-medium text-sm">Alerts</span>
            </div>
            {unreadCount > 0 && (
              <span className="flex items-center justify-center min-w-5 h-5 px-1 rounded-md bg-orizonte-lime/20 text-orizonte-lime text-xs font-bold border border-orizonte-lime/30">
                {unreadCount}
              </span>
            )}
          </button>
          <Link 
            to="/download"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-orizonte-textMuted hover:bg-white/[0.02] hover:text-orizonte-text transition-all duration-300"
          >
            <Download className="w-5 h-5" />
            <span className="font-medium text-sm">Mac App</span>
          </Link>
          <button 
            onClick={() => setIsSettingsOpen(true)}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-orizonte-textMuted hover:bg-white/[0.02] hover:text-orizonte-text transition-all duration-300"
          >
            <Settings className="w-5 h-5" />
            <span className="font-medium text-sm">Settings</span>
          </button>
          <button 
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-orizonte-textMuted hover:bg-white/[0.02] hover:text-orizonte-text transition-all duration-300"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium text-sm">Log out</span>
          </button>
        </div>
      </aside>
    </>
  );
};
